import fetch from 'cross-fetch';
import { fileTypeFromBuffer } from 'file-type';

import { OtherMimeType } from '../constants';
import type { ImageAsset, ImageFormat, ImageMap } from '../types';

const IMAGE_FORMAT_BY_MIME: Partial<Record<string, ImageFormat>> = {
  [OtherMimeType.APNG]: 'png',
  [OtherMimeType.BMP]: 'bmp',
  [OtherMimeType.GIF]: 'gif',
  [OtherMimeType.JPEG]: 'jpeg',
  [OtherMimeType.PNG]: 'png',
  [OtherMimeType.WEBP]: 'webp',
};

type ImageFiles = { filestackUrl: string; id: string };

/**
 * Convert an array buffer to base64 encoded string.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const binary = { bString: '' };
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < bytes.byteLength; i++) binary.bString += String.fromCharCode(bytes[i]);

  return btoa(binary.bString);
}

/**
 * Given an asset at a URL, fetch it and convert it to a base64 encoded string.
 */
export async function urlToBase64Asset(url: string): Promise<string> {
  return arrayBufferToBase64(await fetchAssetBuffer(url));
}

/**
 * Detect an image's format while its raw bytes are available, then encode it for serializers.
 */
export async function arrayBufferToImageAsset(
  buffer: ArrayBuffer | Uint8Array
): Promise<ImageAsset> {
  const fileType = await fileTypeFromBuffer(buffer);

  return {
    base64: arrayBufferToBase64(buffer),
    format: fileType ? (IMAGE_FORMAT_BY_MIME[fileType.mime] ?? null) : null,
  };
}

/**
 * Given an asset at a URL, fetch it and prepare it for image serializers.
 */
export async function urlToImageAsset(url: string): Promise<ImageAsset> {
  return arrayBufferToImageAsset(await fetchAssetBuffer(url));
}

export async function assetsArrayToImageMap(imageFiles: ImageFiles[]): Promise<ImageMap> {
  const imagesWithUrls = imageFiles.filter(asset => Boolean(asset.filestackUrl));
  const imageMap: ImageMap = new Map();

  await Promise.all(
    imagesWithUrls.map(async ({ filestackUrl, id }) => {
      imageMap.set(id, await urlToImageAsset(filestackUrl));
    })
  );

  return imageMap;
}

export const blackColor = '#000000';

async function fetchAssetBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) {
    const { origin, pathname } = new URL(url);
    throw new Error(
      `Failed to fetch image asset (HTTP ${response.status}) from ${origin}${pathname}`
    );
  }
  return response.arrayBuffer();
}
